import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useNavigate, useLocation } from 'react-router-dom'
import { CommonTable } from '@/components/common'
import { chairpersonService } from '@/services'
import { formatUtcDate, formatDisplayTime } from '@/utils/date'
import { DriveDonorsTabShell } from './DriveDonorsTabShell'
import { DonorsSelectionToolbar } from './DonorsSelectionToolbar'
import { DonorsExportButton } from './DonorsExportButton'
import {
  DRIVE_DONORS_DATE_FORMAT,
  DRIVE_DONORS_DATETIME_FORMAT,
  DRIVE_DONORS_EMAIL_SOURCE,
  DRIVE_DONORS_RECIPIENT_MODES,
  DRIVE_DONORS_SELECTED_RECIPIENT_ERROR,
  DRIVE_DONORS_SOURCE_TABS,
  DONOR_EXPORT_CONTACT_HEADERS,
  getDriveDonorsSelectedCount,
  hasDriveDonorsSelectedRecipients,
  navigateToDriveDonorsEmailFlow,
  splitDriveDonorsEmailSelectionFromRows,
} from './constants'
import { pickChairpersonDriveNavState } from '@/utils/chairpersonNavigation'

const CSV_HEADERS = [
  { key: 'donor_name', label: 'Donor' },
  { key: 'donor_number', label: 'Donor Number' },
  { key: 'procedure_name', label: 'Procedure' },
  { key: 'appointment_time', label: 'Appointment Time' },
  { key: 'last_donation_at_account', label: 'Last Donation' },
  ...DONOR_EXPORT_CONTACT_HEADERS,
]

export const ScheduledAppointmentsTab = ({ driveId, accountId }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [rows, setRows] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedRows, setSelectedRows] = useState([])
  const [allDonorsCount, setAllDonorsCount] = useState(null)
  const [loadingSendOptions, setLoadingSendOptions] = useState(false)
  const [emailFlowError, setEmailFlowError] = useState('')
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 25,
    sortBy: undefined,
    sortDirection: undefined,
  })

  const fetchAppointments = useCallback(async () => {
    if (!driveId || !accountId) return
    setLoading(true)
    try {
      const response =
        await chairpersonService.getDriveDonorsScheduledAppointments(
          driveId,
          accountId,
          {
            page: pagination.page + 1,
            limit: pagination.perPage,
          }
        )
      const payload = response?.data ?? {}
      setRows(Array.isArray(payload.appointments) ? payload.appointments : [])
      setTotalRecords(Number(payload.total_count ?? 0))
    } catch (error) {
      console.error('Error fetching scheduled appointments', error)
      setRows([])
      setTotalRecords(0)
    } finally {
      setLoading(false)
    }
  }, [driveId, accountId, pagination.page, pagination.perPage])

  useEffect(() => {
    fetchAppointments()
  }, [fetchAppointments])

  useEffect(() => {
    setSelectedRows([])
  }, [pagination.page, pagination.perPage, driveId])

  useEffect(() => {
    if (!driveId || !accountId) return undefined

    let cancelled = false

    const loadSendCounts = async () => {
      setLoadingSendOptions(true)
      try {
        const preview =
          await chairpersonService.previewDriveDonorsEmailRecipients(
            driveId,
            accountId,
            {
              source_tab: DRIVE_DONORS_SOURCE_TABS.SCHEDULED_APPOINTMENTS,
              source: DRIVE_DONORS_EMAIL_SOURCE,
              recipient_mode: DRIVE_DONORS_RECIPIENT_MODES.ALL_DONORS,
            }
          )

        if (!cancelled) {
          setAllDonorsCount(Number(preview?.data?.will_receive) || 0)
        }
      } catch (error) {
        if (!cancelled) {
          setAllDonorsCount(null)
        }
      } finally {
        if (!cancelled) {
          setLoadingSendOptions(false)
        }
      }
    }

    loadSendCounts()

    return () => {
      cancelled = true
    }
  }, [accountId, driveId])

  const tableData = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        rowKey: row.is_guest
          ? `guest-${row.guest_id}-${row.appointment_time}-${index}`
          : `donor-${row.donor_id}-${row.appointment_time}-${index}`,
        donor_name: row.donor_name ?? 'N/A',
        donor_number: row.is_guest ? 'Guest' : (row.donor_number ?? 'N/A'),
        procedure_name: row.procedure_name ?? 'N/A',
        appointment_time: row.appointment_time
          ? formatDisplayTime(
              row.appointment_time,
              DRIVE_DONORS_DATETIME_FORMAT
            )
          : 'N/A',
        last_donation_at_account: row.last_donation_at_account
          ? formatUtcDate(
              row.last_donation_at_account,
              DRIVE_DONORS_DATE_FORMAT
            )
          : 'N/A',
      })),
    [rows]
  )

  const selectableRows = tableData

  const { donorIds: selectedDonorIds, guestIds: selectedGuestIds } = useMemo(
    () => splitDriveDonorsEmailSelectionFromRows(selectedRows),
    [selectedRows]
  )

  const selectedCount = getDriveDonorsSelectedCount({
    donorIds: selectedDonorIds,
    guestIds: selectedGuestIds,
  })

  const allSelected =
    selectableRows.length > 0 && selectedRows.length === selectableRows.length

  const handleToggleSelectAll = () => {
    setSelectedRows(allSelected ? [] : [...selectableRows])
  }

  const driveNavState = pickChairpersonDriveNavState(location.state)

  const startEmailFlow = useCallback(
    recipientMode => {
      const { donorIds, guestIds } =
        recipientMode === DRIVE_DONORS_RECIPIENT_MODES.SELECTED
          ? splitDriveDonorsEmailSelectionFromRows(selectedRows)
          : { donorIds: [], guestIds: [] }

      if (
        recipientMode === DRIVE_DONORS_RECIPIENT_MODES.SELECTED &&
        !hasDriveDonorsSelectedRecipients({ donorIds, guestIds })
      ) {
        setEmailFlowError(DRIVE_DONORS_SELECTED_RECIPIENT_ERROR)
        return
      }

      setEmailFlowError('')
      navigateToDriveDonorsEmailFlow({
        navigate,
        driveId,
        accountId,
        donorIds,
        guestIds,
        sourceTab: DRIVE_DONORS_SOURCE_TABS.SCHEDULED_APPOINTMENTS,
        recipientMode,
        driveNavState,
      })
    },
    [accountId, driveId, driveNavState, navigate, selectedRows]
  )

  const fetchExportData = useCallback(async () => {
    const response =
      await chairpersonService.getDriveDonorsScheduledAppointments(
        driveId,
        accountId,
        { forExport: 'true' }
      )
    const exportRows = Array.isArray(response?.data?.appointments)
      ? response.data.appointments
      : []

    return exportRows.map(row => ({
      donor_name: row.donor_name,
      donor_number: row.donor_number || 'N/A',
      procedure_name: row.procedure_name || 'N/A',
      appointment_time: row.appointment_time
        ? formatDisplayTime(row.appointment_time, DRIVE_DONORS_DATETIME_FORMAT)
        : 'N/A',
      last_donation_at_account: row.last_donation_at_account
        ? formatUtcDate(row.last_donation_at_account, DRIVE_DONORS_DATE_FORMAT)
        : '',
      email: row.email || '',
      phone: row.phone || '',
      calling_preference: row.calling_preference || '',
      text_preference: row.text_preference || '',
      email_preference: row.email_preference || '',
    }))
  }, [driveId, accountId])

  const columns = [
    { id: 'donor_name', label: 'Donor', minWidth: 180 },
    { id: 'donor_number', label: 'Donor Number', minWidth: 120 },
    { id: 'procedure_name', label: 'Procedure', minWidth: 140 },
    { id: 'appointment_time', label: 'Appointment Time', minWidth: 180 },
    { id: 'last_donation_at_account', label: 'Last Donation', minWidth: 140 },
  ]

  return (
    <DriveDonorsTabShell
      statTitle={`Scheduled Appointments (${totalRecords})`}
      statSubtitle='Donors and guests with appointments scheduled for this drive'
      showEligibilityBanner={false}
      aboutTitle='About Scheduled Appointments'
      aboutText='This list includes donors and guests who have scheduled appointments for this drive.'
      selectionToolbar={
        <DonorsSelectionToolbar
          selectedCount={selectedCount}
          selectionActionsDisabled={selectedCount === 0}
          allSelected={allSelected}
          onToggleSelectAll={handleToggleSelectAll}
          onSendToSelected={() =>
            startEmailFlow(DRIVE_DONORS_RECIPIENT_MODES.SELECTED)
          }
          onSendToAllDonors={() =>
            startEmailFlow(DRIVE_DONORS_RECIPIENT_MODES.ALL_DONORS)
          }
          onClearSelection={() => setSelectedRows([])}
          allDonorsCount={allDonorsCount}
          loadingSendOptions={loadingSendOptions}
          showEligibleOption={false}
        />
      }
      exportAction={
        <DonorsExportButton
          disabled={loading || !totalRecords}
          fetchExportData={fetchExportData}
          csvHeaders={CSV_HEADERS}
          csvFilename={`Drive_Scheduled_Appointments_${driveId}.csv`}
        />
      }
      errorMessage={emailFlowError}
    >
      <CommonTable
        columns={columns}
        data={tableData}
        loading={loading}
        mode='server'
        totalItems={totalRecords}
        showSelect
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        pagination={pagination}
        onPaginationChange={setPagination}
        pageSizeOptions={[10, 25, 50]}
      />
    </DriveDonorsTabShell>
  )
}

ScheduledAppointmentsTab.propTypes = {
  driveId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}
